import React, { InputHTMLAttributes, forwardRef, useId } from 'react';
import { cn } from '../../utils/cn';

export interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      label,
      description,
      size = 'md',
      className,
      disabled,
      checked,
      id,
      onChange,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;

    const sizeConfig: Record<string, { track: string; thumb: string; translate: string }> = {
      sm: { track: 'w-8 h-[18px]', thumb: 'h-3.5 w-3.5', translate: 'translate-x-[14px]' },
      md: { track: 'w-10 h-5', thumb: 'h-4 w-4', translate: 'translate-x-5' },
      lg: { track: 'w-12 h-6', thumb: 'h-5 w-5', translate: 'translate-x-6' },
    };

    const config = sizeConfig[size];

    const toggle = () => {
      if (disabled || !onChange) return;
      const nextChecked = !checked;
      onChange({
        target: { checked: nextChecked, value: nextChecked ? 'on' : '', name: props.name },
        currentTarget: { checked: nextChecked },
      } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
      <div className={cn('flex items-start', className)}>
        <div className="flex items-center h-5">
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(checked)}
            aria-labelledby={label ? `${inputId}-label` : undefined}
            disabled={disabled}
            onClick={toggle}
            className={cn(
              'relative inline-flex flex-shrink-0 rounded-full border-2 border-transparent',
              'transition-colors duration-150',
              'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'cursor-pointer',
              config.track,
              checked ? 'bg-primary-500' : 'bg-neutral-300'
            )}
          >
            <span className="sr-only">{label || 'Toggle'}</span>
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none inline-block rounded-full bg-white shadow',
                'transform transition duration-150',
                config.thumb,
                checked ? config.translate : 'translate-x-0'
              )}
            />
          </button>

          {/* Hidden checkbox for form compatibility / refs */}
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            {...props}
          />
        </div>

        {(label || description) && (
          <div className="ml-3 flex-1">
            {label && (
              <label
                id={`${inputId}-label`}
                htmlFor={inputId}
                onClick={(e) => {
                  // Controlled toggles: drive state via the same path as the switch button
                  e.preventDefault();
                  toggle();
                }}
                className={cn(
                  'block text-sm font-medium text-monday-text',
                  disabled && 'opacity-50 cursor-not-allowed',
                  !disabled && 'cursor-pointer'
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p className={cn('text-xs text-monday-text-muted mt-0.5', disabled && 'opacity-50')}>
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Toggle.displayName = 'Toggle';
